import { Dialog as D } from "radix-ui";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { IconSearch } from "../../components/icons";

/*
 * Shared shell for the quick switcher and command palette: a top-anchored dialog
 * with a combobox input and a grouped listbox. ↑/↓ (or Ctrl+P/N) move, Enter
 * runs, Esc closes (Radix), and the pointer highlights on hover.
 */

export interface PaletteItem {
  id: string;
  label: string;
  sub?: ReactNode;
  icon?: ReactNode;
  /** Right-aligned hint, e.g. a shortcut or "Recent". */
  hint?: ReactNode;
  run: () => void;
}

export interface PaletteSection {
  title?: string;
  items: PaletteItem[];
}

export function PaletteDialog({
  open,
  onOpenChange,
  label,
  placeholder,
  query,
  onQueryChange,
  sections,
  empty,
  footer,
  loading,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  label: string;
  placeholder: string;
  query: string;
  onQueryChange: (q: string) => void;
  sections: PaletteSection[];
  empty: ReactNode;
  footer?: ReactNode;
  loading?: boolean;
}) {
  const id = useId();
  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => setActive(0), [query, open]);
  useEffect(() => {
    if (active >= flat.length) setActive(Math.max(0, flat.length - 1));
  }, [flat.length, active]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const runAt = (i: number) => {
    const item = flat[i];
    if (!item) return;
    onOpenChange(false);
    // Let the dialog close (and return focus) before navigating or opening another overlay.
    requestAnimationFrame(() => item.run());
  };

  const onKey = (e: KeyboardEvent) => {
    const down = e.key === "ArrowDown" || (e.ctrlKey && e.key.toLowerCase() === "n");
    const up = e.key === "ArrowUp" || (e.ctrlKey && e.key.toLowerCase() === "p");
    if (down || up) {
      e.preventDefault();
      if (!flat.length) return;
      setActive((a) => (a + (down ? 1 : -1) + flat.length) % flat.length);
    } else if (e.key === "Home" && e.ctrlKey) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End" && e.ctrlKey) {
      e.preventDefault();
      setActive(flat.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      runAt(active);
    }
  };

  let index = -1;
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="motion-overlay fixed inset-0 z-50 bg-overlay" />
        <D.Content
          aria-label={label}
          className="motion-modal fixed left-1/2 top-[12vh] z-50 flex max-h-[min(70dvh,560px)] w-[calc(100vw-24px)] max-w-xl -translate-x-1/2 flex-col overflow-hidden rounded-xl bg-bg-subtle shadow-pop focus:outline-none"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement).querySelector<HTMLInputElement>("input")?.focus();
          }}
        >
          <D.Title className="sr-only">{label}</D.Title>
          <D.Description className="sr-only">Type to filter, use the arrow keys to choose and Enter to open.</D.Description>
          <div className="flex items-center gap-2.5 border-b border-line px-4">
            <IconSearch className="icon size-[18px] shrink-0 text-fg-3" aria-hidden />
            <input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={onKey}
              placeholder={placeholder}
              role="combobox"
              aria-expanded
              aria-controls={`${id}-list`}
              aria-activedescendant={flat[active] ? `${id}-${active}` : undefined}
              aria-autocomplete="list"
              autoComplete="off"
              spellCheck={false}
              className="h-12 min-w-0 flex-1 bg-transparent text-base text-fg placeholder:text-fg-3 focus:outline-none"
            />
            <kbd className="hidden rounded border border-line-strong px-1.5 text-[11px] font-medium text-fg-3 sm:block">Esc</kbd>
          </div>
          <div ref={listRef} id={`${id}-list`} role="listbox" aria-label={label} className="scroll-y min-h-0 flex-1 p-1.5">
            {flat.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-fg-3">{loading ? "Searching…" : empty}</div>
            ) : (
              sections.map(
                (s, si) =>
                  s.items.length > 0 && (
                    <div key={s.title ?? si} role="group" aria-label={s.title} className={cn(si > 0 && "mt-1.5")}>
                      {s.title && <div className="px-2.5 pb-1 pt-1.5 text-xs font-medium text-fg-3">{s.title}</div>}
                      {s.items.map((item) => {
                        index++;
                        const i = index;
                        const on = i === active;
                        return (
                          <div
                            key={item.id}
                            id={`${id}-${i}`}
                            data-index={i}
                            role="option"
                            aria-selected={on}
                            onPointerMove={() => !on && setActive(i)}
                            onClick={() => runAt(i)}
                            className={cn("flex h-10 cursor-pointer select-none items-center gap-3 rounded-md px-2.5", on ? "bg-surface-2 text-fg" : "text-fg-2")}
                          >
                            {item.icon && <span className="flex size-6 shrink-0 items-center justify-center text-fg-2">{item.icon}</span>}
                            <span className="min-w-0 flex-1 truncate text-sm">
                              <span className={cn(on && "text-fg")}>{item.label}</span>
                              {item.sub && <span className="ml-2 text-xs text-fg-3">{item.sub}</span>}
                            </span>
                            {item.hint && <span className="shrink-0 text-xs text-fg-3">{item.hint}</span>}
                          </div>
                        );
                      })}
                    </div>
                  ),
              )
            )}
          </div>
          {footer && <div className="flex items-center gap-4 border-t border-line px-4 py-2 text-xs text-fg-3">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

export function KeyHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {keys.map((k) => (
        <kbd key={k} className="rounded border border-line-strong px-1 text-[11px] font-medium text-fg-2">
          {k}
        </kbd>
      ))}
      {label}
    </span>
  );
}
